import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import Header from "@/components/Header";
import { formatPrice } from "@/components/ProductCard";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import { CheckCircle, Lock, Bank, UploadSimple, ClockCountdown, XCircle, Copy, CreditCard, ShieldCheck, MapPin, ArrowLeft, Truck } from "@phosphor-icons/react";
import MercadoPagoCardForm from "@/components/MercadoPagoCardForm";

const PLACEHOLDER = "https://images.unsplash.com/photo-1495100497150-fe209c585f50?crop=entropy&cs=srgb&fm=jpg&q=85&w=800";

export default function Checkout() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { clear } = useCart();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [transfer, setTransfer] = useState(null);
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef();

  const loadOrder = () => api.get(`/orders/${orderId}`).then((r) => setOrder(r.data));

  useEffect(() => {
    Promise.all([
      loadOrder(),
      api.get("/settings").then((r) => setTransfer(r.data.payment_methods?.transfer || null)),
    ]).catch(() => toast.error("Orden no encontrada")).finally(() => setLoading(false));
  }, [orderId]);

  const submitReceipt = async () => {
    if (!file) { toast.error("Selecciona el comprobante"); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await api.post("/upload/receipt", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const res = await api.post(`/orders/${orderId}/receipt`, { receipt_image: up.data.path });
      setOrder(res.data);
      setFile(null);
      clear();
      toast.success("Comprobante enviado");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo enviar el comprobante");
    } finally { setSubmitting(false); }
  };

  const onCardResult = (res) => {
    if (res.ok) {
      setOrder(res.order);
      clear();
      if (res.order.status === "paid") toast.success("¡Pago aprobado!");
      else toast.info("Tu pago está en revisión. Te avisaremos al confirmarse.");
    } else {
      toast.error(res.message || "El pago fue rechazado");
    }
  };

  const copy = (txt) => { navigator.clipboard?.writeText(txt); toast.success("Copiado"); };

  if (loading) return <div className="App min-h-screen bg-background"><Header /><div className="max-w-5xl mx-auto p-8"><div className="h-80 bg-white rounded-xl animate-pulse" /></div></div>;
  if (!order) return <div className="App min-h-screen bg-background"><Header /><div className="max-w-2xl mx-auto p-8">Orden no encontrada</div></div>;

  const OrderSummary = () => (
    <div className="mt-4 border-t border-black/10 pt-4 space-y-2 text-sm">
      <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{formatPrice(order.subtotal)}</span></div>
      {order.discount > 0 && <div className="flex justify-between text-[hsl(var(--meli-green))]"><span>Descuento {order.coupon_code ? `(${order.coupon_code})` : ""}</span><span>-{formatPrice(order.discount)}</span></div>}
      {order.credit_applied > 0 && <div className="flex justify-between text-emerald-600"><span>Saldo a favor</span><span>-{formatPrice(order.credit_applied)}</span></div>}
      <div className="flex justify-between"><span className="text-muted-foreground">Envío</span><span>{order.shipping === 0 ? <span className="text-[hsl(var(--meli-green))] font-semibold">Gratis</span> : formatPrice(order.shipping)}</span></div>
      <div className="flex justify-between text-xl font-extrabold border-t border-black/10 pt-3 mt-1"><span>Total</span><span data-testid="checkout-total">{formatPrice(order.total)}</span></div>
    </div>
  );

  const Items = ({ compact }) => (
    <div className={compact ? "space-y-2.5" : "mt-3 space-y-3"}>
      {order.items.map((it, i) => (
        <div key={i} className="flex items-center gap-3">
          <div className="relative shrink-0">
            <img src={it.product_image ? fileUrl(it.product_image) : PLACEHOLDER} alt="" className={`${compact ? "w-12 h-12" : "w-14 h-14"} rounded-lg object-cover border border-black/5`} onError={(e)=>{e.currentTarget.src=PLACEHOLDER;}} />
            <span className="absolute -top-2 -right-2 bg-[hsl(var(--brand-navy))] text-white text-[10px] font-bold min-w-[18px] h-[18px] rounded-full flex items-center justify-center px-1">{it.quantity}</span>
          </div>
          <div className="flex-1 min-w-0"><p className="font-medium text-sm leading-tight line-clamp-2">{it.product_title}</p>{it.variant_name && <p className="text-xs text-muted-foreground">{it.variant_name}</p>}</div>
          <span className="text-sm font-semibold whitespace-nowrap">{formatPrice(it.subtotal)}</span>
        </div>
      ))}
    </div>
  );

  const addr = order.shipping_address || {};
  const SummaryAside = () => (
    <aside className="lg:sticky lg:top-6 h-fit space-y-4">
      <div className="bg-white rounded-xl nb-shadow p-5">
        <h3 className="font-extrabold tracking-tight flex items-center justify-between">Tu pedido <span className="text-xs font-semibold text-muted-foreground">{order.order_number}</span></h3>
        <div className="mt-4"><Items compact /></div>
        <OrderSummary />
      </div>
      {(addr.street || addr.recipient) && (
        <div className="bg-white rounded-xl nb-shadow p-5">
          <p className="text-xs uppercase tracking-wide text-muted-foreground font-bold flex items-center gap-1.5 mb-2"><MapPin weight="bold" size={14} className="text-primary" /> Envío a</p>
          <p className="text-sm font-semibold">{addr.recipient}</p>
          <p className="text-sm text-muted-foreground">{[addr.street, addr.number, addr.city, addr.state, addr.zip].filter(Boolean).join(", ")}</p>
          {addr.phone && <p className="text-sm text-muted-foreground">Tel. {addr.phone}</p>}
        </div>
      )}
      <div className="flex items-center justify-center gap-4 text-[11px] text-muted-foreground px-2">
        <span className="flex items-center gap-1"><ShieldCheck weight="fill" size={14} className="text-[hsl(var(--meli-green))]" /> Pago seguro</span>
        <span className="flex items-center gap-1"><Truck weight="fill" size={14} className="text-primary" /> Envío con guía</span>
        <span className="flex items-center gap-1"><Lock weight="bold" size={13} /> Datos cifrados</span>
      </div>
    </aside>
  );

  const Stepper = ({ step }) => (
    <div className="flex items-center justify-center gap-2 text-xs font-semibold mb-6">
      {[["Carrito", 1], ["Pago", 2], ["Confirmación", 3]].map(([label, n], i) => (
        <React.Fragment key={label}>
          {i > 0 && <span className={`h-px w-6 ${step >= n ? "bg-primary" : "bg-black/15"}`} />}
          <span className={`flex items-center gap-1.5 ${step >= n ? "text-foreground" : "text-muted-foreground"}`}>
            <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] ${step > n ? "bg-[hsl(var(--meli-green))] text-white" : step === n ? "bg-primary text-white" : "bg-black/10"}`}>{step > n ? "✓" : n}</span>
            <span className="hidden sm:inline">{label}</span>
          </span>
        </React.Fragment>
      ))}
    </div>
  );

  const terminal = order.status === "paid"
    || (order.status === "pending_transfer" && order.receipt_image)
    || (order.payment_type !== "transfer" && order.status !== "paid" && order.mp_payment_id);

  return (
    <div className="App min-h-screen bg-background">
      <Header />
      <div className={`mx-auto px-4 sm:px-6 py-8 ${terminal ? "max-w-xl" : "max-w-5xl"}`}>
        <Stepper step={terminal ? 3 : 2} />

        {!terminal && (
          <button onClick={() => navigate("/carrito")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors" data-testid="back-to-cart">
            <ArrowLeft weight="bold" size={15} /> Volver al carrito
          </button>
        )}

        {/* PAID */}
        {order.status === "paid" && (
          <div className="bg-white rounded-xl nb-shadow-lg p-8 text-center" data-testid="payment-success">
            <div className="w-20 h-20 rounded-full bg-[hsl(var(--meli-green))]/15 flex items-center justify-center mx-auto auth-in">
              <CheckCircle weight="fill" size={52} className="text-[hsl(var(--meli-green))]" />
            </div>
            <h1 className="text-2xl font-extrabold tracking-tight mt-4">¡Compra confirmada!</h1>
            <p className="text-muted-foreground mt-2">Tu pago de <b>{formatPrice(order.total)}</b> fue aprobado. Pedido {order.order_number}.</p>
            <div className="bg-secondary/60 rounded-xl p-4 mt-6 text-left"><Items /><OrderSummary /></div>
            <div className="flex gap-2 mt-6">
              <button onClick={() => navigate("/cuenta")} className="flex-1 nb-btn bg-secondary text-foreground py-3 hover:brightness-95">Ver mi pedido</button>
              <button onClick={() => navigate("/")} data-testid="continue-shopping-button" className="flex-1 nb-btn bg-primary text-primary-foreground py-3 hover:brightness-110">Seguir comprando</button>
            </div>
          </div>
        )}

        {/* TRANSFER — waiting verification (receipt sent) */}
        {order.status === "pending_transfer" && order.receipt_image && (
          <div className="bg-white rounded-xl nb-shadow-lg p-8 text-center" data-testid="transfer-pending">
            <div className="w-20 h-20 rounded-full bg-amber-100 flex items-center justify-center mx-auto">
              <ClockCountdown weight="fill" size={50} className="text-amber-500" />
            </div>
            <h1 className="text-2xl font-extrabold tracking-tight mt-4">Comprobante enviado</h1>
            <p className="text-muted-foreground mt-2">Estamos verificando tu transferencia. Te avisaremos al confirmar el pedido {order.order_number}.</p>
            <div className="mt-5 text-left">
              <p className="text-xs font-semibold text-[hsl(var(--meli-green))] flex items-center gap-1 mb-2" data-testid="receipt-uploaded-badge"><CheckCircle weight="fill" size={15} /> Comprobante subido</p>
              <a href={fileUrl(order.receipt_image)} target="_blank" rel="noreferrer" className="block">
                <img src={fileUrl(order.receipt_image)} alt="Comprobante" className="max-h-48 rounded-lg border border-black/10 mx-auto" />
              </a>
            </div>
            <div className="bg-secondary/60 rounded-xl p-4 mt-6 text-left"><Items /><OrderSummary /></div>
            <button onClick={() => navigate("/cuenta")} className="mt-6 w-full nb-btn bg-primary text-primary-foreground py-3 hover:brightness-110">Ver mis compras</button>
          </div>
        )}

        {/* CARD IN REVIEW (in_process / pending) */}
        {order.payment_type !== "transfer" && order.status !== "paid" && order.mp_payment_id && (
          <div className="bg-white rounded-xl nb-shadow-lg p-8 text-center" data-testid="card-pending">
            <div className="w-20 h-20 rounded-full bg-amber-100 flex items-center justify-center mx-auto">
              <ClockCountdown weight="fill" size={50} className="text-amber-500" />
            </div>
            <h1 className="text-2xl font-extrabold tracking-tight mt-4">Pago en revisión</h1>
            <p className="text-muted-foreground mt-2">Estamos confirmando tu pago del pedido {order.order_number}. Te avisaremos en cuanto se acredite.</p>
            <div className="bg-secondary/60 rounded-xl p-4 mt-6 text-left"><Items /><OrderSummary /></div>
            <button onClick={() => navigate("/cuenta")} className="mt-6 w-full nb-btn bg-primary text-primary-foreground py-3 hover:brightness-110">Ver mis compras</button>
          </div>
        )}

        {/* ACTIVE PAYMENT — two columns */}
        {!terminal && (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6 items-start">
            <div className="space-y-4">

              {/* CARD PAYMENT (Mercado Pago, embedded, no redirect) */}
              {order.payment_type !== "transfer" && (
                <div className="bg-white rounded-xl nb-shadow overflow-hidden" data-testid="mercadopago-checkout">
                  <div className="bg-[hsl(var(--brand-navy))] px-6 py-4 flex items-center justify-between">
                    <span className="font-extrabold text-white text-lg tracking-tight flex items-center gap-2"><CreditCard weight="bold" size={20} /> Pago con tarjeta</span>
                    <span className="text-[11px] font-bold bg-white/15 text-white rounded-full px-2.5 py-1 flex items-center gap-1"><ShieldCheck weight="fill" size={13} /> Protegido</span>
                  </div>
                  <div className="p-6">
                    <p className="text-sm text-muted-foreground mb-4">Paga con crédito o débito sin salir de la tienda. Hasta <b>3 meses sin intereses</b> según tu banco.</p>
                    <MercadoPagoCardForm order={order} onResult={onCardResult} />
                    <p className="text-xs text-center text-muted-foreground mt-4 flex items-center justify-center gap-1"><Lock weight="bold" size={12} /> Procesado de forma segura por Mercado Pago</p>
                  </div>
                </div>
              )}

              {/* TRANSFER — upload receipt (also when rejected) */}
              {order.payment_type === "transfer" && (order.status === "rejected" || (order.status === "pending_transfer" && !order.receipt_image)) && (
                <div className="bg-white rounded-xl nb-shadow overflow-hidden" data-testid="transfer-checkout">
                  <div className="bg-[hsl(var(--brand-navy))] px-6 py-4 flex items-center gap-2">
                    <Bank weight="bold" size={20} className="text-white" />
                    <span className="font-extrabold text-white text-lg tracking-tight">Pago por transferencia</span>
                  </div>
                  <div className="p-6">
                    {order.status === "rejected" && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 flex items-start gap-2">
                        <XCircle weight="fill" size={18} className="text-red-500 mt-0.5" />
                        <div><p className="text-sm font-semibold text-red-700">Comprobante rechazado</p><p className="text-xs text-red-600">{order.rejection_reason}. Puedes volver a subir un comprobante válido.</p></div>
                      </div>
                    )}
                    <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">Datos para la transferencia</p>
                    <div className="mt-2 rounded-lg border border-black/10 divide-y divide-black/5 text-sm">
                      {[["Banco", transfer?.bank_name], ["Titular", transfer?.account_holder], ["CBU/CVU", transfer?.cbu], ["Alias", transfer?.alias]].filter(([, v]) => v).map(([k, v]) => (
                        <div key={k} className="flex items-center justify-between px-3 py-2">
                          <span className="text-muted-foreground">{k}</span>
                          <span className="font-medium flex items-center gap-2">{v}<button onClick={() => copy(v)} className="text-primary hover:opacity-70"><Copy size={14} /></button></span>
                        </div>
                      ))}
                      <div className="flex items-center justify-between px-3 py-2 bg-secondary/40">
                        <span className="text-muted-foreground">Monto</span><span className="font-extrabold">{formatPrice(order.total)}</span>
                      </div>
                      <div className="flex items-center justify-between px-3 py-2">
                        <span className="text-muted-foreground">Referencia</span><span className="font-medium">{order.order_number}</span>
                      </div>
                    </div>
                    {transfer?.instructions && <p className="text-xs text-muted-foreground mt-2">{transfer.instructions}</p>}

                    <div className="mt-5">
                      <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold mb-2">Subir comprobante</p>
                      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="receipt-file-input" />
                      <button onClick={() => fileRef.current?.click()} className="w-full border border-dashed border-black/25 rounded-lg py-4 text-sm text-muted-foreground hover:border-primary hover:text-primary transition-colors flex items-center justify-center gap-2" data-testid="receipt-choose-button">
                        <UploadSimple weight="bold" size={18} /> {file ? "Cambiar imagen" : "Elegir imagen del comprobante"}
                      </button>
                      {file && (
                        <div className="mt-3 flex items-center gap-3 bg-[hsl(var(--meli-green))]/10 rounded-lg p-2" data-testid="receipt-preview">
                          <img src={URL.createObjectURL(file)} alt="preview" className="w-14 h-14 rounded object-cover" />
                          <div className="text-xs">
                            <p className="font-semibold text-[hsl(var(--meli-green))] flex items-center gap-1"><CheckCircle weight="fill" size={13} /> Listo para enviar</p>
                            <p className="text-muted-foreground truncate max-w-[200px]">{file.name}</p>
                          </div>
                        </div>
                      )}
                    </div>

                    <button onClick={submitReceipt} disabled={submitting || !file} data-testid="submit-receipt-button" className="mt-5 w-full nb-btn bg-primary text-primary-foreground py-4 text-lg hover:brightness-110 disabled:opacity-50">
                      {submitting ? "Enviando…" : "Ya realicé la transferencia"}
                    </button>
                    <p className="text-xs text-center text-muted-foreground mt-3">El pedido se confirmará luego de verificar tu comprobante.</p>
                  </div>
                </div>
              )}
            </div>

            <SummaryAside />
          </div>
        )}
      </div>
    </div>
  );
}
